import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';

import { getPulseRoleTrend } from '@/api/pulse';
import { getInvoiceForecast, getInvoiceHistory, getMembershipHistory } from '@/api/billing';
import { getMemberHistory, listMembers } from '@/api/members';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendChart, type TrendPoint } from '@/components/ui/trend-chart';
import { formatMoneyRounded } from '@/lib/money';
import { collapsePulseRounds } from '@/lib/pulse-trend';

// MetricDetailDialog — the drill-down behind each of the three home KPI tiles.
//
// Each tile answers "what is it now"; this answers "how has it gone", which
// needs a longer window than a tile can hold. Every metric gets the same shape:
// a chart, a small grid of supporting numbers, and a link to the page that owns
// the metric — so the dialog is a stop on the way to acting, not a dead end.
//
// Queries are keyed so the tile and the dialog SHARE the cache: opening the
// dialog costs only the extra history call, never a refetch of what the tile
// already has.

export type MetricKey = 'enps' | 'money' | 'members';

export function MetricDetailDialog({
  metric,
  open,
  onOpenChange,
  slug,
  token,
}: {
  metric: MetricKey;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  token: string | null;
}) {
  const { t } = useTranslation('common');
  const target =
    metric === 'enps'
      ? { to: `/orgs/${slug}/pulse`, cta: t('home.kpi.detail.goToPulse') }
      : metric === 'money'
        ? { to: `/orgs/${slug}/billing`, cta: t('home.kpi.detail.goToBilling') }
        : { to: `/orgs/${slug}/members`, cta: t('home.kpi.detail.goToMembers') };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={t('common.close', { defaultValue: 'Close' })}>
        <DialogHeader>
          <DialogTitle>{t(`home.kpi.detail.${metric}.title`)}</DialogTitle>
          <DialogDescription>{t(`home.kpi.detail.${metric}.blurb`)}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {/* Mounted only while open, so a closed dialog costs no requests. */}
          {open && metric === 'enps' && <EnpsDetail slug={slug} token={token} />}
          {open && metric === 'money' && <MoneyDetail slug={slug} token={token} />}
          {open && metric === 'members' && <MembersDetail slug={slug} token={token} />}
        </DialogBody>
        <DialogFooter>
          <Button asChild>
            <Link to={target.to}>
              {target.cta}
              <ArrowRight size={16} weight="bold" aria-hidden />
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- shared bits -----------------------------------------------------------

function ChartSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-[180px] w-full rounded-xl" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
      {text}
    </p>
  );
}

// Facts is the supporting-numbers grid. Deliberately plain: the chart carries
// the shape, these carry the exact values someone would otherwise hover for.
function Facts({
  items,
}: {
  // hint is explicitly `| undefined` because the project runs
  // exactOptionalPropertyTypes: callers build these objects conditionally.
  items: { label: string; value: string; hint?: string | undefined }[];
}) {
  return (
    <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {items.map((f) => (
        <div key={f.label} className="rounded-xl border border-border bg-background p-3">
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {f.label}
          </dt>
          <dd className="mt-0.5 font-heading text-lg font-bold tabular-nums">{f.value}</dd>
          {f.hint && <dd className="text-xs text-muted-foreground">{f.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}

// monthLabel renders an RFC3339 month start as a short month name, with the
// year appended each January so a 12+ month axis doesn't lose its footing.
function monthLabel(iso: string, locale: string): string {
  const d = new Date(iso);
  const m = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(d);
  return d.getUTCMonth() === 0 ? `${m} ${String(d.getUTCFullYear()).slice(2)}` : m;
}

// --- eNPS ------------------------------------------------------------------

// One point per pulse RUN, not per calendar month: eNPS only exists where a
// survey ran, so the x axis is survey dates. Runs below the anonymity floor
// come back null and stay gaps.
function EnpsDetail({ slug, token }: { slug: string; token: string | null }) {
  const { t, i18n } = useTranslation('common');
  const q = useQuery({
    queryKey: ['home-enps', slug],
    queryFn: () => getPulseRoleTrend(token, slug, 'all'),
    enabled: !!token && !!slug,
  });
  if (q.isLoading) return <ChartSkeleton />;

  // One point per ROUND, not per role-campaign — see lib/pulse-trend.
  const runs = collapsePulseRounds(q.data?.points ?? []);
  const points: TrendPoint[] = runs.map((p) => ({
    label: new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' }).format(
      new Date(p.at),
    ),
    value: p.enps ?? null,
  }));
  const present = runs.filter((p): p is typeof p & { enps: number } => p.enps != null);
  if (present.length === 0) {
    return <EmptyState text={t('home.kpi.detail.enps.empty')} />;
  }
  const latest = present[present.length - 1]!;
  const first = present[0]!;
  const prior = present.length > 1 ? present[present.length - 2]! : null;
  const best = present.reduce((a, b) => (b.enps > a.enps ? b : a));

  return (
    <>
      <TrendChart
        points={points}
        variant="line"
        color="var(--lime)"
        // eNPS runs -100..100 and clusters high; a forced zero baseline would
        // flatten every real movement into noise.
        zeroBased={false}
        format={(v) => String(Math.round(v))}
        ariaLabel={t('home.kpi.detail.enps.chartLabel')}
      />
      <Facts
        items={[
          {
            label: t('home.kpi.detail.enps.current'),
            value: String(Math.round(latest.enps)),
            hint: t('home.kpi.detail.enps.responses', { count: latest.n }),
          },
          {
            label: t('home.kpi.detail.enps.change'),
            value:
              prior != null
                ? `${latest.enps - prior.enps > 0 ? '+' : ''}${Math.round(latest.enps - prior.enps)}`
                : '—',
            hint: t('home.kpi.detail.enps.sincePrevious'),
          },
          {
            label: t('home.kpi.detail.enps.best'),
            value: String(Math.round(best.enps)),
          },
          {
            label: t('home.kpi.detail.enps.runs'),
            value: String(present.length),
            hint: t('home.kpi.detail.enps.since', {
              date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                new Date(first.at),
              ),
            }),
          },
        ]}
      />
    </>
  );
}

// --- money -----------------------------------------------------------------

function MoneyDetail({ slug, token }: { slug: string; token: string | null }) {
  const { t, i18n } = useTranslation('common');
  const hist = useQuery({
    queryKey: ['home-invoice-history', slug],
    queryFn: () => getInvoiceHistory(token, slug, 12),
    enabled: !!token && !!slug,
  });
  // Shares the tile's cache entry — no second request for the 30-day figure.
  const fc = useQuery({
    queryKey: ['home-forecast', slug],
    queryFn: () => getInvoiceForecast(token, slug, 30),
    enabled: !!token && !!slug,
  });
  if (hist.isLoading) return <ChartSkeleton />;

  const months = hist.data?.months ?? [];
  const currency = hist.data?.currency || fc.data?.currency || '';
  const money = (minor: number) => formatMoneyRounded(minor, currency, i18n.language);
  const anyActivity = months.some((m) => m.billed_minor > 0 || m.collected_minor > 0);
  if (!anyActivity) {
    return <EmptyState text={t('home.kpi.detail.money.empty')} />;
  }

  const points: TrendPoint[] = months.map((m) => ({
    label: monthLabel(m.month, i18n.language),
    // Collected is the primary series — cash that actually arrived is the
    // honest headline; billed sits behind it as the claim.
    value: m.collected_minor,
    secondary: m.billed_minor,
  }));
  const billed = months.reduce((s, m) => s + m.billed_minor, 0);
  const collected = months.reduce((s, m) => s + m.collected_minor, 0);
  const rate = billed > 0 ? Math.round((collected / billed) * 100) : null;

  return (
    <>
      <TrendChart
        points={points}
        variant="bars"
        color="var(--lime)"
        format={(v) => money(v)}
        ariaLabel={t('home.kpi.detail.money.chartLabel')}
        primaryLabel={t('home.kpi.detail.money.collected')}
        secondaryLabel={t('home.kpi.detail.money.billed')}
      />
      <Facts
        items={[
          {
            label: t('home.kpi.detail.money.incoming30'),
            value: fc.data ? money(fc.data.incoming_minor) : '—',
            hint: fc.data ? t('home.kpi.detail.money.invoiceCount', { count: fc.data.count }) : undefined,
          },
          {
            label: t('home.kpi.detail.money.overdue'),
            value: fc.data ? money(fc.data.overdue_minor) : '—',
          },
          {
            label: t('home.kpi.detail.money.collected12'),
            value: money(collected),
          },
          {
            label: t('home.kpi.detail.money.collectionRate'),
            value: rate != null ? `${rate}%` : '—',
            hint: t('home.kpi.detail.money.ofBilled', { amount: money(billed) }),
          },
        ]}
      />
    </>
  );
}

// --- members ---------------------------------------------------------------

// "Members" here means PAID members — people who have settled the club's
// membership fee for the fiscal year — using the same rule as the members
// list's paid filter and the member-card scan page (billing.MembershipService).
// The roster curve is shown underneath as context: the gap between the two IS
// the collection story, and hiding either number would flatter the club.
export function MembersDetail({ slug, token }: { slug: string; token: string | null }) {
  const { t, i18n } = useTranslation('common');
  const mem = useQuery({
    queryKey: ['home-membership', slug],
    queryFn: () => getMembershipHistory(token, slug, 5),
    enabled: !!token && !!slug,
  });
  const roster = useQuery({
    queryKey: ['home-member-history', slug],
    queryFn: () => getMemberHistory(token, slug, 12),
    enabled: !!token && !!slug,
  });
  // Shares the tile's cache entry for the roster total.
  const total = useQuery({
    queryKey: ['home-members-count', slug],
    queryFn: () => listMembers(token, slug, { limit: 1 }),
    enabled: !!token && !!slug,
  });
  if (mem.isLoading || roster.isLoading) return <ChartSkeleton />;

  const years = mem.data?.years ?? [];
  const currentYear = years.find((y) => y.fiscal_year === mem.data?.current_fiscal_year);
  const rosterMonths = roster.data?.months ?? [];
  const rosterTotal = total.data?.total ?? rosterMonths[rosterMonths.length - 1]?.total ?? 0;

  const paidPoints: TrendPoint[] = years.map((y) => ({
    label: String(y.fiscal_year),
    value: y.paid_members,
    secondary: y.billed_members,
  }));
  const rosterPoints: TrendPoint[] = rosterMonths.map((m) => ({
    label: monthLabel(m.month, i18n.language),
    value: m.total,
  }));
  const joined12 = rosterMonths.reduce((s, m) => s + m.joined, 0);
  const left12 = rosterMonths.reduce((s, m) => s + m.left, 0);
  const paid = currentYear?.paid_members ?? 0;
  const billedMembers = currentYear?.billed_members ?? 0;
  const coverage = rosterTotal > 0 ? Math.round((paid / rosterTotal) * 100) : null;

  return (
    <>
      {/* No membership-fee type configured → every paid count is 0 for a reason
          that has nothing to do with the members. Say so instead of charting
          zeros, and still show the roster curve, which is real either way. */}
      {mem.data && !mem.data.configured ? (
        <p className="mb-4 rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t('home.kpi.detail.members.notConfigured')}
        </p>
      ) : (
        <>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('home.kpi.detail.members.paidHeading')}
          </h3>
          <TrendChart
            points={paidPoints}
            variant="bars"
            color="var(--lime)"
            format={(v) => String(Math.round(v))}
            ariaLabel={t('home.kpi.detail.members.paidChartLabel')}
            primaryLabel={t('home.kpi.detail.members.paid')}
            secondaryLabel={t('home.kpi.detail.members.billed')}
          />
        </>
      )}

      <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('home.kpi.detail.members.rosterHeading')}
      </h3>
      {rosterPoints.length > 0 ? (
        <TrendChart
          points={rosterPoints}
          variant="line"
          color="var(--ink)"
          format={(v) => String(Math.round(v))}
          ariaLabel={t('home.kpi.detail.members.rosterChartLabel')}
        />
      ) : (
        <EmptyState text={t('home.kpi.detail.members.empty')} />
      )}

      <Facts
        items={[
          {
            label: t('home.kpi.detail.members.paidThisYear'),
            value: mem.data?.configured ? String(paid) : '—',
            hint: mem.data
              ? t('home.kpi.detail.members.fiscalYear', { year: mem.data.current_fiscal_year })
              : undefined,
          },
          {
            label: t('home.kpi.detail.members.unpaid'),
            // Billed but not yet paid. Members never invoiced at all are a
            // different problem and are not folded in here.
            value: mem.data?.configured ? String(Math.max(billedMembers - paid, 0)) : '—',
            hint: mem.data?.configured
              ? t('home.kpi.detail.members.ofBilled', { count: billedMembers })
              : undefined,
          },
          {
            label: t('home.kpi.detail.members.roster'),
            value: String(rosterTotal),
            hint:
              coverage != null && mem.data?.configured
                ? t('home.kpi.detail.members.coverage', { percent: coverage })
                : undefined,
          },
          {
            label: t('home.kpi.detail.members.net12'),
            value: `${joined12 - left12 > 0 ? '+' : ''}${joined12 - left12}`,
            hint: t('home.kpi.detail.members.joinedLeft', { joined: joined12, left: left12 }),
          },
        ]}
      />
    </>
  );
}
